/**
 * OrientationPermissionManager
 *
 * Manages iOS DeviceOrientation permission with improved UX:
 * - Uses sessionStorage to avoid asking multiple times per session
 * - Provides clear feedback to users
 * - Handles iOS 13+ permission requirements
 *
 * NOTE: iOS does NOT allow persisting this permission across app launches
 * (security/privacy feature by Apple). We can only cache it per session.
 */

export class OrientationPermissionManager {
  private static readonly SESSION_KEY = 'blobo_orientation_permission_granted';

  /**
   * Check if device requires permission (iOS 13+)
   */
  public static requiresPermission(): boolean {
    return (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof (DeviceOrientationEvent as any).requestPermission === 'function'
    );
  }

  /**
   * Check if permission was already granted this session
   * Uses sessionStorage (not localStorage) because iOS resets permission on app launch
   */
  public static isGrantedThisSession(): boolean {
    if (!this.requiresPermission()) {
      return true; // Android or older iOS - no permission needed
    }

    const granted = sessionStorage.getItem(this.SESSION_KEY);
    return granted === 'true';
  }

  /**
   * Request orientation permission (iOS 13+)
   * Must be called from a user gesture (button click, etc.)
   *
   * @returns Promise<boolean> - true if granted, false if denied
   */
  public static async requestPermission(): Promise<boolean> {
    // Check if already granted this session
    if (this.isGrantedThisSession()) {
      console.log('📱 Orientation permission already granted this session');
      return true;
    }

    // Android or older iOS - no permission needed
    if (!this.requiresPermission()) {
      sessionStorage.setItem(this.SESSION_KEY, 'true');
      return true;
    }

    // iOS 13+ - request permission
    try {
      console.log('📱 Requesting iOS orientation permission...');
      const response = await (DeviceOrientationEvent as any).requestPermission();

      if (response === 'granted') {
        console.log('✅ Orientation permission granted');
        sessionStorage.setItem(this.SESSION_KEY, 'true');
        return true;
      } else {
        console.warn('⚠️ Orientation permission denied');
        sessionStorage.setItem(this.SESSION_KEY, 'false');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting orientation permission:', error);
      sessionStorage.setItem(this.SESSION_KEY, 'false');
      return false;
    }
  }

  /**
   * Clear session permission (for testing)
   */
  public static clearSession(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Get user-friendly message about why permission is needed
   */
  public static getPermissionMessage(): string {
    return 'Blobo needs access to your device motion to enable tilt controls. This is required for gameplay.';
  }
}
